<template>
  <section class="py-12 bg-white sm:py-16">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Heading -->
      <p
        class="inline-block rounded-full bg-green-50 px-3 py-1 text-sm font-semibold uppercase tracking-wide text-wiq-green"
      >
        WIQ Fee Calculator
      </p>
      <h1
        class="mt-3 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl"
      >
        Know exactly what lands in your bank account
      </h1>
      <p class="mt-4 max-w-3xl text-lg text-gray-600">
        Set the price you charge in WrestlingIQ and see exactly what the parent pays, what the
        card processor and platform take, and what lands in your club's bank account.
      </p>

      <div class="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <!-- INPUT -->
        <div class="bg-white rounded-xl shadow-lg p-8">
          <h2
            class="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-900"
          >
            <span class="inline-block w-1.5 h-4 rounded-sm bg-wiq-green"></span
            >Your price
          </h2>

          <div class="mt-6">
            <label for="fc-base" class="text-sm font-medium text-gray-700"
              >Base price (what you set in WrestlingIQ)</label
            >
            <div class="mt-2 relative rounded-lg shadow-sm">
              <div
                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4"
              >
                <span class="text-gray-500 text-lg">$</span>
              </div>
              <input
                id="fc-base"
                v-model.number="basePrice"
                type="number"
                min="0"
                step="1"
                inputmode="decimal"
                class="block w-full rounded-lg border border-gray-300 py-3 pl-8 pr-4 text-lg font-semibold text-gray-900 focus:border-wiq-green focus:ring-wiq-green"
                placeholder="200"
              />
            </div>
            <p class="mt-2 text-sm text-gray-500">
              Try the price of a monthly membership or a single registration.
            </p>
          </div>

          <!-- Who pays what -->
          <div class="mt-8 border-t border-gray-100 pt-6">
            <h3 class="text-xs font-bold uppercase tracking-wider text-gray-500">
              Who pays what
            </h3>
            <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div
                class="bg-purple-50 border-2 border-purple-200 rounded-lg overflow-hidden"
              >
                <div class="bg-purple-600 text-purple-50 text-center py-1.5">
                  <span class="text-xs font-bold tracking-widest uppercase">Parent pays</span>
                </div>
                <div class="p-4 text-center">
                  <p class="text-sm font-semibold text-gray-700">WrestlingIQ fee</p>
                  <p class="mt-1 text-3xl font-bold text-gray-900">2%</p>
                  <p class="mt-2 text-xs text-gray-600">Added on top of your price</p>
                </div>
              </div>
              <div
                class="bg-blue-50 border-2 border-blue-200 rounded-lg overflow-hidden"
              >
                <div class="bg-blue-600 text-blue-50 text-center py-1.5">
                  <span class="text-xs font-bold tracking-widest uppercase">Club pays</span>
                </div>
                <div class="p-4 text-center">
                  <p class="text-sm font-semibold text-gray-700">Card processing</p>
                  <p class="mt-1 text-3xl font-bold text-gray-900">2.9% + 30&cent;</p>
                  <p class="mt-2 text-xs text-gray-600">Per successful transaction</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- BREAKDOWN -->
        <div class="bg-white rounded-xl shadow-lg p-8">
          <h2
            class="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-900"
          >
            <span class="inline-block w-1.5 h-4 rounded-sm bg-wiq-green"></span
            >The breakdown
          </h2>

          <!-- Net payout headliner -->
          <div class="mt-6">
            <p class="font-mono text-xs uppercase tracking-wide text-gray-500">
              Net to your bank
            </p>
            <p
              class="mt-1 text-5xl font-extrabold leading-none text-wiq-green tabular-nums"
            >
              {{ money(fees.netToClub) }}
            </p>
            <p class="mt-3 text-sm text-gray-500">
              on a {{ money(fees.basePrice) }} price — the parent pays
              {{ money(fees.parentTotal) }}
            </p>
          </div>

          <!-- Line items -->
          <dl class="mt-8 space-y-3">
            <div
              class="flex justify-between items-baseline border-b border-gray-100 pb-3"
            >
              <dt class="text-sm text-gray-600">Base price</dt>
              <dd class="font-mono text-base font-medium text-gray-900 tabular-nums">
                {{ money(fees.basePrice) }}
              </dd>
            </div>
            <div
              class="flex justify-between items-baseline border-b border-gray-100 pb-3"
            >
              <dt class="text-sm text-gray-600">
                + WrestlingIQ fee (2%)
                <span
                  class="mt-1 inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-2 py-0.5 text-xs font-semibold text-purple-700"
                >
                  Parent pays this — $0 out of your pocket
                </span>
              </dt>
              <dd class="font-mono text-base font-medium text-purple-600 tabular-nums">
                {{ money(fees.wiqFee) }}
              </dd>
            </div>
            <div
              class="flex justify-between items-baseline border-b border-gray-200 pb-3"
            >
              <dt class="text-sm font-medium text-gray-800">Parent pays</dt>
              <dd class="font-mono text-base font-semibold text-gray-900 tabular-nums">
                {{ money(fees.parentTotal) }}
              </dd>
            </div>
            <div
              class="flex justify-between items-baseline border-b border-gray-100 pb-3"
            >
              <dt class="text-sm text-gray-600">
                &minus; Card processing (2.9% + $0.30)
                <span
                  class="mt-1 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700"
                >
                  Club pays this
                </span>
              </dt>
              <dd class="font-mono text-base font-medium text-blue-600 tabular-nums">
                {{ money(fees.processingFee) }}
              </dd>
            </div>
            <div class="flex justify-between items-baseline pt-1">
              <dt class="text-sm font-bold text-gray-900">Net to your bank</dt>
              <dd class="font-mono text-lg font-bold text-wiq-green tabular-nums">
                {{ money(fees.netToClub) }}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { feeBreakdown } from './lib/fee_calc';

export default {
  name: 'fee-calculator-panel',
  data() {
    return {
      basePrice: 200,
    };
  },
  computed: {
    fees() {
      return feeBreakdown(this.basePrice);
    },
  },
  methods: {
    // Two-decimal currency: fees land on cents ($5.62), so we can't reuse the
    // whole-dollar formatter from the MRR panel.
    money(value) {
      if (!Number.isFinite(value)) return '$0.00';
      return `$${value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
};
</script>
